import { Card } from "@supliful/common"

import { Alert } from "../components/Alert"
import { Badge } from "../components/Badge"
import { Breadcrumb } from "../components/Breadcrumb"
import { Button } from "../components/Button"
import { Checkbox } from "../components/Checkbox"
import { Drawer } from "../components/Drawer"
import { GridListCard } from "../components/GridListCard"
import { Heading } from "../components/Heading"
import { Input } from "../components/Input"
import { Link } from "../components/Link"
import { Menu } from "../components/Menu"
import { Modal } from "../components/Modal"
import { Popover } from "../components/Popover"
import { Progress } from "../components/Progress"
import { Radio } from "../components/Radio"
import { Select } from "../components/Select"
import { Stepper } from "../components/Stepper"
import { Table } from "../components/Table"
import { Tabs } from "../components/Tabs"
import { Tag } from "../components/Tag"
import { Text } from "../components/Text"
import { Textarea } from "../components/Textarea"
import { Tooltip } from "../components/Tooltip"

export const suplifulTheme = {
  breakpoints: {
    xs: "20rem", // 320px. iPhone SE
    "2xs": "22.5rem", // 360px. Galaxy >S21 360px, iPhone >11 390px, Pixel >5 393px, iPhone 15 Pro Max 430px
    sm: "30rem", // 480px
    md: "48rem", // 768px iPad Mini, iPad Pro 11 834px
    lg: "62rem", // 992px iPad Pro 12.9 1024px
    xl: "80rem", // 1280px
    "2xl": "96rem", // 1536px
  },
  config: {
    initialColorMode: "light",
  },
  styles: {
    global: {
      body: {
        background: "neutrals.100",
        overflowX: "hidden",
      },
      img: {
        display: "inline",
      },
    },
  },
  components: {
    Alert,
    Badge,
    Breadcrumb,
    Button,
    Card,
    Checkbox,
    Drawer,
    GridListCard,
    Heading,
    Input,
    Link,
    Menu,
    Modal,
    Popover,
    Progress,
    Radio,
    Select,
    Stepper,
    Tag,
    Text,
    Textarea,
    Tabs,
    Table,
    Tooltip,
  },
  colors: {
    primary: {
      // The same as "emerald" palette in the AI generated prototype
      25: "#F4F8F3",
      50: "#DFF4EA",
      100: "#bbe4cc",
      200: "#8ed3ab",
      300: "#5bc38b",
      400: "#2ab672", // Supliful green
      500: "#2aae73", // Primary/700 in our Figma design system
      600: "#218968", // Primary/900 in our Figma design system
      700: "#1c784b", // button:down
      800: "#165837",
      900: "#165837",
    },
    secondary: {
      50: "#1f644d",
      100: "#1b5943",
      200: "#174b38",
      300: "#123c2c",
      400: "#123c2c",
      500: "#1D1D1D", // button:up
      600: "#474747", // button:hover
      700: "#1A1A1A", // button:down
      800: "#252525",
      900: "#252525",
    },
    tertiary: {
      50: "#fafafa",
      100: "#fafafa",
      200: "#fafafa",
      300: "#fafafa",
      400: "#fafafa",
      500: "#f7f7f7", // button:up
      600: "#ececec", // button:hover
      700: "#dddddd", // button:down
      750: "#bababa",
      800: "#9b9b9b",
      850: "#888888",
      900: "#777777",
    },
    main: {
      // Primary color palette in our Figma design system
      100: "#E7F1E1",
      300: "#B7DEAF",
      500: "#6BC78A",
      700: "#2AAE73",
      900: "#218968",
    },
    error: {
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      300: "#fca5a5",
      400: "#f87171",
      500: "#ef4444",
      600: "#dc2626",
      700: "#b91c1c",
      800: "#991b1b",
      900: "#7f1d1d",
    },
    // @deprecated Use `neutrals` instead
    gray: {
      50: "#FAFAFA", // button gray
      100: "#F7F7F7", // active category filter
      200: "#E6E6E6", // border color Neutrals/200
      500: "#464646", // text color
      600: "#3c3c3c",
      700: "#323232",
      800: "#1D1D1F", // dark color
    },
    // True grayscale colors from light to dark
    neutrals: {
      20: "#FDFDFD",
      50: "#FAFAFA",
      100: "#F7F7F7",
      200: "#E6E6E6",
      300: "#D9D9D9",
      400: "#BFBFBF",
      500: "#A6A6A6",
      600: "#737373",
      700: "#595959",
      800: "#404040",
      850: "#212121",
      900: "#1D1D1D",
    },
    blackAlpha: {
      50: "rgba(0, 0, 0, 0.04)",
      100: "rgba(0, 0, 0, 0.06)",
      200: "rgba(0, 0, 0, 0.08)",
      250: "rgba(0, 0, 0, 0.12)",
      300: "rgba(0, 0, 0, 0.16)",
      400: "rgba(0, 0, 0, 0.24)",
      500: "rgba(0, 0, 0, 0.36)",
      600: "rgba(0, 0, 0, 0.48)",
      700: "rgba(0, 0, 0, 0.64)",
      800: "rgba(0, 0, 0, 0.80)",
      900: "rgba(0, 0, 0, 0.92)",
    },
    amber: {
      50: "#fffbeb",
      100: "#fef3c7",
      200: "#fde68a",
      300: "#fcd34d",
      400: "#fbbf24",
      500: "#f59e0b",
      600: "#d97706",
      700: "#b45309",
      800: "#92400e",
      900: "#78350f",
    },
    yellowBright: {
      50: "#fefce8",
      100: "#ffebb8",
      200: "#ffde89",
      300: "#ffd257",
      400: "#ffc734",
      500: "#ffbe1f",
      600: "#ffb01c",
      700: "#ff9d1b",
      800: "#fe8d19",
      900: "#fd6d17",
    },
    yellow: {
      50: "#fefce8",
      100: "#fef9c3",
      200: "#fef08a",
      300: "#fde047",
      400: "#facc15",
      500: "#eab308",
      600: "#ca8a04",
      700: "#a16207",
      800: "#854d0e",
      900: "#713f12",
      950: "#281304",
    },
    sun: {
      100: "#FFF7D9",
      300: "#FFEBB8",
      500: "#ECCE8C",
      700: "#CCA652",
      900: "#8A6922",
    },
    red: {
      50: "rgba(182, 42, 42, 0.1)", // #F5EAEA
      300: "#B62A2A",
      500: "#DD493A",
      800: "#EA001E",
    },
    pink: {
      100: "#FFF2F2",
      300: "#EDD8D8",
      500: "#E0BBB4",
      700: "#C76D6D",
      900: "#8A2222",
    },
    brown: {
      50: "#f7ebe2",
      100: "#e1cec3",
      200: "#c7aea1",
      300: "#ad8f7d",
      400: "#997661",
      500: "#845e46",
      600: "#78543f",
      700: "#684836",
      800: "#5a3b2d",
      900: "#4a2d23",
    },
    sky: {
      100: "#E4F2F4",
      300: "#BFDADE",
      500: "#91B3C5",
      700: "#4283A6",
      900: "#22668A",
    },
    blue: {
      50: "#eff6ff",
      100: "#dbeafe",
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb",
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
      950: "#0c142e",
    },
    disabled: {
      50: "#F3F4F6",
      200: "#D1D5DB",
      700: "#6B7280",
    },
    grayBlue: "#F0F8F9",
  },
  borders: {
    listItem: "1px solid var(--chakra-colors-neutrals-200)",
    inputGroup: "1px dashed #E7E7E7",
    section: "1px solid var(--chakra-colors-neutrals-200)",
  },

  shadows: {
    outline: "none",
    primaryButton: "0px 15px 25px 0 #2AB67340",
    tooltip: "0 8px 24px 0 #0f0f0f30",
    card: "0 -1px 2px 0 var(--chakra-colors-blackAlpha-100)",
    listItem: "0 0 5px 0 var(--chakra-colors-blackAlpha-200)",
    listItemHover: "0px 0px 15px var(--chakra-colors-blackAlpha-100)",
    inputField: "0px 0px 30px rgba(0, 0, 0, 0.07)",
    section: "0 1px 2px 0 rgba(0, 0, 0, .05)",
  },
  fonts: {
    body: "Manrope Variable",
    heading: "Manrope Variable, sans-serif",
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  fontSizes: {
    h1: "2.5rem",
    h2: "2.25rem",
    h3: "2rem",
    h4: "1.5rem",
    h5: "1.25rem",
    "5xl": "2.25rem", // 40px
    "4xl": "2rem", // 32px
    "3xl": "1.75rem", // 28px
    "2xl": "1.5rem", // 24px
    xl: "1.25rem", // 20px
    lg: "1.125rem", // 18px
    md: "1rem", // 16px
    sm: "0.875rem", // 14px
    xs: "0.75rem", // 12px
    "2xs": "0.625rem", // 10px
    "3xs": "0.5625rem", // 9px
  },
  transition: {
    default: "all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1)",
  },
  space: {
    "2xs": "4px",
    sm: "12px",
    md: "16px",
    lg: "24px",
    xl: "48px",
  },
  semanticTokens: {
    colors: {
      text: {
        action: "main.900",
        actionDark: "neutrals.900",
        body: "neutrals.700",
        disabled: "disabled.700",
        heading: "neutrals.900",
        information: "sky.900",
        success: "main.900",
      },
      border: {
        disabled: "#D1D5DB",
        primary: "neutrals.200",
        action: "main.900",
      },
      surface: {
        status: {
          success: "#DCFCE7",
        },
        action: "main.900",
        actionHover: "main.700",
        brand: "main.100",
        disabled: "disabled.50",
        neutral: "neutrals.100",
        success: "main.100",
        page: "neutrals.50",
      },
      icon: {
        neutral: "#737373",
        status: {
          success: "#16A34A",
        },
      },
      success: {
        25: "primary.25",
        50: "primary.50",
        100: "primary.100",
        200: "primary.200",
        300: "primary.300",
        400: "primary.400",
        500: "primary.500",
        600: "primary.600",
        700: "primary.700",
        800: "primary.800",
        900: "primary.900",
      },
      warning: {
        50: "amber.50",
        100: "amber.100",
        200: "amber.200",
        300: "amber.300",
        400: "amber.400",
        500: "amber.500",
        600: "amber.600",
        700: "amber.700",
        800: "amber.800",
        900: "amber.900",
      },
      info: {
        50: "blue.50",
        100: "blue.100",
        200: "blue.200",
        300: "blue.300",
        400: "blue.400",
        500: "blue.500",
        600: "blue.600",
        700: "blue.700",
        800: "blue.800",
        900: "blue.900",
      },
      freeplan: {
        50: "neutrals.50",
        100: "neutrals.100",
        200: "neutrals.200",
        300: "neutrals.300",
        400: "neutrals.400",
        500: "neutrals.500",
        600: "neutrals.600",
        700: "neutrals.700",
        800: "neutrals.800",
        900: "neutrals.900",
      },
      proplan: {
        50: "yellow.50",
        100: "yellow.100",
        200: "yellow.200",
        300: "yellow.300",
        400: "yellow.400",
        500: "yellow.500",
        600: "yellow.600",
        700: "yellow.700",
        800: "yellow.800",
        900: "yellow.900",
      },
      enterpriseplan: {
        50: "blue.50",
        100: "blue.100",
        200: "blue.200",
        300: "blue.300",
        400: "blue.400",
        500: "blue.500",
        600: "blue.600",
        700: "blue.700",
        800: "blue.800",
        900: "blue.900",
      },
    },
  },
  layerStyles: {
    card: {
      bg: "white",
      borderRadius: "2xl",
    },
    listItem: {
      borderRadius: { base: "lg", lg: "2xl" },
      background: "white",
      transition: "default",
      _hover: {
        boxShadow: "listItemHover",
      },
      _focusVisible: {
        outline: "2px solid",
        outlineColor: "main.700",
      },
    },
  },
  textStyles: {
    body1: {
      fontSize: "0.875rem",
      lineHeight: "1.85",
    },
    body2: {
      fontSize: "0.75rem",
      lineHeight: "1.84",
    },
    heading: {
      fontWeight: 700,
      lineHeight: "1.2",
      letterSpacing: "-1%",
    },
    h2: {
      fontWeight: 700,
      fontSize: "h2",
      lineHeight: "1.35",
      letterSpacing: "-1%",
    },
    h3: {
      fontWeight: 700,
      fontSize: "h3",
      lineHeight: "1.44",
    },
    h4: {
      fontWeight: 700,
      fontSize: "h4",
      lineHeight: "1.33",
    },
    h5: {
      fontWeight: 700,
      fontSize: "h5",
      lineHeight: "1.5",
    },
    h6: {
      fontWeight: 700,
      fontSize: "md",
      lineHeight: "1.25",
    },
  },
}
